import React, {useContext, useEffect, useMemo, useState} from "react";
import {toast} from "react-toastify";
import {
    Autocomplete,
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {grey} from "@mui/material/colors";
import {menuItemsInfo} from "../editMenuItems";
import {IconArrowsDiagonal, IconPlus, IconX} from "@tabler/icons-react";
import {v4 as uuidv4} from "uuid";
import {PersonaModal} from "../../../../../components/modals/PersonaModal";
import {WebSettingsContext} from "../../../../../context/WebSettingsContext";
import {TopicRepository} from "../../../../../repositories/TopicRepository";
import {UserInputSettings} from "../settings/components/user-input-settings";
import {WatchdogSelection} from "../settings/WatchdogSelection";
import {NodeType} from "../../../../../model/ModelData";
import {Context} from "../../../../../context/AppContext"

export const SettingsBoosterTab = ({node, setNode, onUpdate, project, nodes}) => {
    const [expandPersona, setExpandPersona] = useState(false);
    const [expandUserInputMessage, setExpandUserInputMessage] = useState(false);

    const {settings, modelIds} = useContext(WebSettingsContext);

    const {state, getAllServersWithDetails} = useContext(Context);
    const {mcpServers, mcpLoading, kbTopics} = state;


    const [topics, setTopics] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState(null);

    const isKbNode = [NodeType.GEN_AI_WORKFLOW, NodeType.CHATBOT, NodeType.QA].includes(node?.type)


    useEffect(() => {
        setSelectedOrganization(project?.settings?.organization || null);

        if (isKbNode) return;

        TopicRepository.getAll()
            .then(response => setTopics(response.data))
            .catch(err => console.log(err));
    }, [node?.type, project?.settings?.organization]);

    const activeTopics = isKbNode ? kbTopics : topics;

    useEffect(() => {
        if (!isKbNode || !node?.data?.topic_id) return;

        const selectedTopic = activeTopics?.find(t => String(t?.id) === String(node.data.topic_id));

        const isInactive = selectedTopic?.server?.status && selectedTopic.server.status !== "online";

        if (isInactive) {
            toast.warning(`Topic "${selectedTopic.name}" server is ${selectedTopic.server.status} — may fail at runtime.`);
        }
    }, [node?.data?.topic_id, activeTopics]);


    useEffect(() => {
        if (!project?.id) return;
        console.log("project", project)
        if (!project?.configuration?.mcp_registry) return;
        console.log("mcp servers: ", mcpServers)
        if (mcpServers?.length > 0) return;

        getAllServersWithDetails(project.id);
    }, [project?.id]);


    // const allTools = (mcpServers || []).flatMap(server =>
    //     (server.tools || []).map(tool => ({
    //         name: tool.name,
    //         description: tool.description,
    //         serverName: server.name,
    //         serverId: server.id,
    //         serverActive: server.active,
    //     }))
    // );
    const allTools = [
        ...(project?.tools || []).map(tool => ({
            name: tool?.function?.name,
            description: tool?.function?.description,
            serverName: "Custom Tools",
            serverId: null,
            serverActive: true,
            source: "custom",
            raw: tool,
        })),
        ...(mcpServers || []).flatMap(server => {
            return (server.tools || []).map(tool => ({
                name: tool.name,
                description: tool.description,
                serverName: server.name,
                serverId: server.id,
                serverActive: server.active,
                source: "mcp",
                raw: tool,
            }));
        })
    ];
    console.log("All tools ", allTools)

    useEffect(() => {
        const attached = node?.data?.skills;
        if (!attached?.length || !project?.skills) return;

        let changed = false;
        const synced = [];
        for (const s of attached) {
            const fresh = project.skills.find(ps => ps.id === s.id);
            if (!fresh) {
                changed = true;
                continue;
            }
            if (
                fresh.content !== s.content ||
                fresh.name !== s.name ||
                fresh.description !== s.description
            ) {
                changed = true;
                synced.push({
                    id: s.id,
                    name: fresh.name,
                    description: fresh.description || "",
                    content: fresh.content || "",
                });
            } else {
                synced.push(s);
            }
        }

        if (changed) {
            onUpdate({...node, data: {...node.data, skills: synced}});
        }
    }, [project?.skills, node?.id]);

    // useEffect(() => {
    //     setSelectedOrganization(project?.settings?.organization || null);
    //     const fetchTopics = node?.type === NodeType.QA || NodeType.GEN_AI_WORKFLOW
    //         ? KnowledgeRepository.getTopics()
    //         : TopicRepository.getAll();
    //     fetchTopics
    //         .then(response => {
    //             setTopics(response.data);
    //             console.log(response.data)
    //         })
    //         .catch(err => console.log(err));
    // }, []);

    const onChangeData = (key, value) => {
        setNode(prev => {
            const state = {...prev};
            state.data[key] = value;

            return state;
        })
    }

    const {primary, secondary} = menuItemsInfo.settings_booster;

    return (
        <>
            <PersonaModal
                handleClose={() => setExpandPersona(false)}
                open={expandPersona}
                onSuccess={(content) => {
                    onChangeData('system_prompt', content)
                    onUpdate({...node, data: {...(node?.data || {}), system_prompt: content}})
                }}
                text={typeof node?.data?.system_prompt === "string" ? node.data.system_prompt : ""}
            />
            <PersonaModal
                handleClose={() => setExpandUserInputMessage(false)}
                open={expandUserInputMessage}
                onSuccess={(content) => {
                    onChangeData('user_message', content)
                    onUpdate({...node, data: {...(node?.data || {}), user_message: content}})
                }}
                text={typeof node?.data?.user_message === "string" ? node.data.user_message : ""}
            />
            <Box sx={{borderBottom: 1, borderColor: "divider", pb: 0.5}}>
                <Typography fontWeight={700} sx={{pb: 1}}>{primary}</Typography>
                <Typography variant={"body2"} fontSize={13}>{secondary}</Typography>
            </Box>

            <TextField
                value={node?.data?.max_ttl || ""}
                size={"small"}
                type={"number"}
                label={"Max TTL"}
                placeholder={"Enter number"}
                helperText={"Maximum attempts to understand user"}
                onChange={e => onChangeData("max_ttl", e.target.value)}
                onBlur={() => onUpdate(node)}
            />
            <Box sx={{px: 1}}>
                <FormControlLabel
                    size={"small"}
                    control={(
                        <Checkbox checked={!!node?.data?.hint} size={"small"} sx={{p: 0.5}}
                                  onChange={(event => onChangeData("hint", event.target.checked))}
                                  onBlur={() => onUpdate(node)}/>
                    )} label={`Use Hint`}
                />
            </Box>
            <Box sx={{px: 1}}>
                <FormControlLabel
                    size={"small"}
                    control={(
                        <Checkbox checked={!!node?.data?.share_conversation_context} size={"small"} sx={{p: 0.5}}
                                  onChange={(event => onChangeData("share_conversation_context", event.target.checked))}
                                  onBlur={() => onUpdate(node)}/>
                    )} label={`Share Conversation Context`}
                />
            </Box>
            <Box sx={{px: 1}}>
                <FormControlLabel
                    size={"small"}
                    control={(
                        <Checkbox checked={!!node?.data?.music_on_hold} size={"small"} sx={{p: 0.5}}
                                  onChange={(event => onChangeData("music_on_hold", event.target.checked))}
                                  onBlur={() => onUpdate(node)}/>
                    )} label={`Music on Hold`}
                />
            </Box>

            {
                node?.data?.share_conversation_context &&
                <Box sx={{px: 1}}>
                    <TextField
                        type={"number"}
                        value={node?.data?.conversation_buffer_window}
                        size={"small"}
                        label={"Conversation buffer window"}
                        placeholder={"Buffer window"}
                        // helperText={"Ask for confirmation"}
                        onChange={(e) => onChangeData('conversation_buffer_window', e.target.value)}
                        onBlur={() => onUpdate(node)}
                    />
                </Box>
            }

            <Box>
                <Typography fontWeight={700} fontSize={13}>Function Tools</Typography>
                <Typography fontWeight={400} color={grey[500]} fontSize={11}>Enable the AI model to call external
                    functions and
                    retrieve dynamic data during conversations</Typography>
            </Box>
            {/*<Autocomplete*/}
            {/*  renderInput={(props) => <TextField {...props} size={"small"} label={`Tools`}/>}*/}
            {/*  options={project?.tools} multiple*/}
            {/*  getOptionLabel={option => option?.function?.name}*/}
            {/*  value={selectedTools || []}*/}
            {/*  onChange={(e, newValue) => onChangeData("tools", newValue?.map(i => i?.id))}*/}
            {/*/>*/}
            <Autocomplete
                renderInput={(props) => <TextField {...props} size={"small"} label={`Tools Object`}/>}
                options={allTools.filter(tool => !node?.data?.tools_obj?.some(obj => obj.name === tool?.name))}
                groupBy={(option) => option.serverName}
                getOptionLabel={option => option?.name || ""}
                renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{display: "flex", alignItems: "center", gap: 1}}>
                        <Box sx={{
                            width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                            bgcolor: option.serverActive ? "success.main" : "warning.main"
                        }}/>
                        <Typography fontSize={13}>{option.name}</Typography>
                        {option.source === "custom" && (
                            <Typography fontSize={10} color="text.secondary" sx={{ml: "auto"}}>
                                custom
                            </Typography>
                        )}
                        {!option.serverActive && (
                            <Typography fontSize={10} color="warning.main" sx={{ml: "auto"}}>
                                server inactive
                            </Typography>
                        )}
                    </Box>
                )}
                multiple={false}
                blurOnSelect
                clearOnBlur
                loading={mcpLoading}
                value={null}
                onChange={async (e, newValue) => {
                    if (!newValue) return;

                    const updatedNode = {...node, data: {...node.data}};
                    const currentToolsObj = updatedNode.data.tools_obj || [];

                    if (newValue.source === "custom") {
                        updatedNode.data.tools_obj = [...currentToolsObj, {
                            name: newValue.name,
                            id: newValue.raw?.id,
                            flush: true,
                            source: "custom",
                        }];
                        onUpdate(updatedNode);
                    } else {
                        updatedNode.data.tools_obj = [...currentToolsObj, {
                            name: newValue.name,
                            server_id: newValue.serverId,
                            flush: true,
                            tool_details: newValue.raw,
                            source: "mcp",
                        }];
                        onUpdate(updatedNode);
                    }
                }}
            />
            {node?.data?.tools_obj?.length > 0 && (
                <Table size="small" sx={{
                    maxWidth: "100%",
                    border: `1px solid ${grey[200]}`,
                    borderRadius: 1,
                    "& .MuiTableCell-root": {borderColor: grey[100], py: 0.5, px: 1}
                }}>
                    <TableHead>
                        <TableRow sx={{bgcolor: grey[50]}}>
                            <TableCell sx={{fontWeight: 600, fontSize: 11, color: grey[600]}}>Tool</TableCell>
                            <TableCell align="center"
                                       sx={{fontWeight: 600, fontSize: 11, color: grey[600]}}>Flush</TableCell>
                            <TableCell align="center" sx={{width: 32}}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {node?.data?.tools_obj?.map((tool, index) => {
                            const isCustom = tool.source === "custom" || !tool.server_id;
                            const server = !isCustom ? mcpServers?.find(s => s.id === tool.server_id) : null;
                            const matchedTool = !isCustom
                                ? allTools.find(t => t.name === tool.name && t.serverId === tool.server_id && t.source === "mcp")
                                : null;

                            const isMissing = !isCustom && !mcpLoading && !matchedTool;
                            const isInactive = !isCustom && !isMissing && (
                                (server && !server.active) || matchedTool?.toolActive === false
                            );

                            return (
                                <TableRow key={`${tool.name}-${index}`}>
                                    <TableCell>
                                        <Tooltip title={
                                            isCustom
                                                ? `Custom tool — ${tool.name}`
                                                : isMissing
                                                    ? `${server?.name || "Unknown server"} — ${tool.name} · tool no longer exists`
                                                    : isInactive
                                                        ? `${server?.name || "Unknown server"} — ${tool.name} · inactive`
                                                        : `${server?.name || "Unknown server"} — ${tool.name}`
                                        }>
                                            <Stack direction="row" alignItems="center" gap={0.5}>
                                                {!isCustom && (isMissing || isInactive) && (
                                                    <Box sx={{
                                                        width: 7, height: 7, borderRadius: "50%",
                                                        bgcolor: isMissing ? "error.main" : "warning.main",
                                                        flexShrink: 0
                                                    }}/>
                                                )}
                                                <Typography noWrap fontSize={12}
                                                            sx={{maxWidth: "160px"}}>{tool.name}</Typography>
                                            </Stack>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox
                                            checked={tool.flush}
                                            size="small"
                                            sx={{p: 0.25}}
                                            onChange={(e) => {
                                                const updatedNode = {...node};
                                                const updatedToolsObj = [...updatedNode.data.tools_obj];
                                                updatedToolsObj[index] = {...tool, flush: e.target.checked};
                                                updatedNode.data.tools_obj = updatedToolsObj;
                                                onUpdate(updatedNode);
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            sx={{p: 0.25}}
                                            onClick={() => {
                                                const updatedNode = {...node};
                                                updatedNode.data.tools_obj = updatedNode.data.tools_obj.filter((_, i) => i !== index);
                                                onUpdate(updatedNode);
                                            }}
                                        >
                                            <IconX size={13}/>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
            <Box>
                <Typography fontWeight={700} fontSize={13}>Skills</Typography>
                <Typography fontWeight={400} color={grey[500]} fontSize={11}>
                    Attach reusable prompt templates to this node
                </Typography>
            </Box>
            <Autocomplete
                renderInput={(props) => <TextField {...props} size={"small"} label={`Skills`}/>}
                options={(project?.skills || []).filter(
                    skill => !node?.data?.skills?.some(s => s.id === skill.id)
                )}
                getOptionLabel={option => option?.name || ""}
                renderOption={(props, option) => (
                    <Box component="li" {...props}>
                        <Stack>
                            <Typography fontSize={13}>{option.name}</Typography>
                            {option.description && (
                                <Typography fontSize={10} color={grey[500]} noWrap sx={{maxWidth: 240}}>
                                    {option.description}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                )}
                multiple={false}
                blurOnSelect
                clearOnBlur
                value={null}
                onChange={(e, newValue) => {
                    if (!newValue) return;
                    const updatedNode = {...node, data: {...node.data}};
                    const current = updatedNode.data.skills || [];
                    updatedNode.data.skills = [...current, {
                        id: newValue.id,
                        name: newValue.name,
                        description: newValue.description || "",
                        content: newValue.content || "",
                    }];
                    onUpdate(updatedNode);
                }}
            />
            {node?.data?.skills?.length > 0 && (
                <Table size="small" sx={{maxWidth: "80%"}}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{p: 0.5}}>Skill Name</TableCell>
                            <TableCell padding="checkbox" sx={{p: 0.5}} align="center"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {node?.data?.skills?.map((skill, index) => (
                            <TableRow key={`${skill.id}-${index}`}>
                                <TableCell padding="checkbox" sx={{px: 0.5, py: 1}}>
                                    <Tooltip title={skill.description || ""}>
                                        <Typography noWrap sx={{maxWidth: "200px"}}>{skill.name}</Typography>
                                    </Tooltip>
                                </TableCell>
                                <TableCell padding="checkbox" sx={{px: 0.5, py: 1}} align="center">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            const updatedNode = {...node, data: {...node.data}};
                                            updatedNode.data.skills = updatedNode.data.skills.filter((_, i) => i !== index);
                                            onUpdate(updatedNode);
                                        }}
                                    >
                                        <IconX size={15}/>
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            {
                [NodeType.GEN_AI_WORKFLOW, NodeType.CHATBOT].includes(node?.type) &&
                <>
                    <Typography fontWeight={700} fontSize={13}>Parameter</Typography>
                    <FormControl size={"small"} fullWidth>
                        <InputLabel id="demo-entity-select-label">Parameter Entity</InputLabel>
                        <Select
                            labelId="demo-entity-select-label"
                            id="demo-entity-select"
                            value={node?.data?.parameter?.entity_type}
                            label="Parameter Entity"
                            onChange={(e) => {
                                const entity = project?.entities?.find(i => i?.name === e?.target?.value);
                                console.log(entity);
                                onChangeData('parameter', {
                                    entity_type: entity?.name || "",
                                    description: entity?.system_prompt || null,
                                    name: node?.data?.parameter?.name || ""
                                })
                            }}
                            onBlur={() => onUpdate(node)}
                        >
                            <MenuItem value={""}><em>None</em></MenuItem>
                            {
                                project?.entities?.map(entity => <MenuItem key={entity?.id}
                                                                           value={entity?.name}>{entity?.name}</MenuItem>)
                            }
                        </Select>
                    </FormControl>
                    <TextField
                        value={node?.data?.parameter?.name || ""}
                        size={"small"}
                        label={"Parameter name"}
                        placeholder={"Enter the name of the parameter"}
                        helperText={"With this name you will be able to further access the variable."}
                        onChange={(e) => onChangeData('parameter', {
                            entity_type: node?.data?.parameter?.entity_type || "",
                            name: e.target.value || ""
                        })}
                        onBlur={() => onUpdate(node)}
                    />
                </>
            }
            <Typography fontWeight={700} fontSize={13}>AI Properties</Typography>
            <FormControl size={"small"}>
                <InputLabel id={`model-label`}
                            sx={{textTransform: "capitalize"}}>Model</InputLabel>
                <Select
                    labelId={`model-label`}
                    id={`model-select`}
                    value={node?.data?.model || "none"}
                    label={`Model`}
                    onChange={(e => onChangeData("model", e.target.value === "none" ? null : e.target.value))}
                    onBlur={() => onUpdate(node)}
                >
                    {/*{settings?.model?.map(model => {*/}
                    {/*  return (*/}
                    {/*    <MenuItem key={model} value={model}>{model}</MenuItem>*/}
                    {/*  )*/}
                    {/*})}*/}
                    {modelIds?.map(model => {
                        return (
                            <MenuItem key={model} value={model}>{model}</MenuItem>
                        )
                    })}
                </Select>
            </FormControl>
            <FormControl size={"small"}>
                <InputLabel id={`topic-label`} sx={{textTransform: "capitalize"}}>Topic</InputLabel>
                <Select
                    labelId={`topic-label`}
                    id={`topic-select`}
                    label={"Topic"}
                    value={node?.data?.topic_id}
                    onBlur={() => onUpdate(node)}
                    onChange={e => {
                        const value = e.target.value;
                        setNode(prev => {
                            const state = {...prev};
                            const topic = activeTopics?.find(i => String(i?.id) === String(value));
                            state.data.topic_id = value === "" ? null : value;
                            state.data.topic_name = topic?.name || null;
                            state.data.slot_id = null;
                            return state;
                        })
                    }}
                >
                    <MenuItem value={""}><em>None</em></MenuItem>
                    {isKbNode
                        ? activeTopics?.map(item => {
                            const isInactive = item?.server?.status && item.server.status !== "online";
                            return (
                                <MenuItem key={item?.id} value={item?.id}>
                                    <Stack direction={"row"} alignItems={"center"} gap={1} sx={{width: "100%"}}>
                                        {isInactive && (
                                            <Tooltip title={`Server status: ${item.server.status}`}>
                                                <Box sx={{
                                                    width: 8, height: 8, borderRadius: "50%",
                                                    bgcolor: "warning.main", flexShrink: 0
                                                }}/>
                                            </Tooltip>
                                        )}
                                        <Typography fontSize={13} noWrap>{item?.name}</Typography>
                                        {isInactive && (
                                            <Typography fontSize={10} color="warning.main" sx={{ml: "auto"}}>
                                                inactive
                                            </Typography>
                                        )}
                                    </Stack>
                                </MenuItem>
                            );
                        })
                        : selectedOrganization && activeTopics
                        ?.filter(item => item?.organization?.id === selectedOrganization || !item?.organization)
                        ?.map(item => (
                            <MenuItem key={item?.id} value={item?.id}>
                                {item?.name}
                            </MenuItem>
                        ))
                    }
                </Select>
            </FormControl>

            {node?.data?.topic_id &&
                <>
                    <Box sx={{px: 1}}>
                        <Stack direction={"row"} gap={2} alignItems={"center"} justifyContent={"space-between"}>
                            <Typography fontWeight={600} fontSize={13}>Topic Score Threshold</Typography>
                            <Typography fontSize={12}>{node?.data?.score_threshold || 0}</Typography>
                        </Stack>

                        <Slider
                            sx={{flex: 1}} size={"small"}
                            value={node?.data?.score_threshold || 0}
                            min={0} max={1} step={0.01}
                            onChange={e => onChangeData("score_threshold", e.target.value)}
                            onBlur={() => onUpdate(node)}
                        />

                    </Box>
                    <Box sx={{px: 1}}>
                        <Stack direction={"row"} gap={2} alignItems={"center"} justifyContent={"space-between"}>
                            <Typography fontWeight={600} fontSize={13}>Topic Results</Typography>

                            <Typography fontSize={12}>{node?.data?.num_results || 0}</Typography>
                        </Stack>
                        <Slider
                            sx={{flex: 1}} size={"small"}
                            value={node?.data?.num_results || 0}
                            min={0} max={100} step={1}
                            onChange={e => onChangeData("num_results", e.target.value)}
                            onBlur={() => onUpdate(node)}
                        />
                    </Box>
                </>
            }
            <Box>
                <Typography fontWeight={700} fontSize={13}>GenAI Goal</Typography>
                <TextField
                    value={node?.data?.genai_goal}
                    size={"small"} multiline maxRows={3}
                    placeholder={"GenAI Goal"} fullWidth
                    // helperText={"Ask for confirmation"}
                    onChange={(e) => onChangeData('genai_goal', e.target.value)}
                    onBlur={() => onUpdate(node)}
                />
            </Box>
            <Box>
                <Stack direction={"row"} gap={1} justifyContent={"space-between"} alignItems={"center"} sx={{pb: 0.5}}>
                    <Typography fontWeight={700} fontSize={13}>System Prompt</Typography>
                    <Box>
                        <IconButton onClick={() => setExpandPersona(true)}>
                            <IconArrowsDiagonal size={15}/>
                        </IconButton>
                    </Box>
                </Stack>
                <TextField
                    value={typeof node?.data?.system_prompt === "string" ? node.data.system_prompt : ""}
                    size={"small"}
                    fullWidth
                    placeholder={"Describe persona"}
                    multiline
                    minRows={3}
                    maxRows={9}
                    onClick={() => setExpandPersona(true)}
                />
            </Box>
            <SystemPromptVariables
                node={node} setNode={setNode} project={project}
                nodes={nodes}
                onChangeData={onChangeData}
                onUpdate={onUpdate}
            />
            <Box>
                <Stack direction={"row"} gap={1} justifyContent={"space-between"} alignItems={"center"} sx={{pb: 0.5}}>
                    <Typography fontWeight={700} fontSize={13}>User Input</Typography>
                    <Box>
                        <IconButton onClick={() => setExpandUserInputMessage(true)}>
                            <IconArrowsDiagonal size={15}/>
                        </IconButton>
                    </Box>
                </Stack>
                <TextField
                    value={typeof node?.data?.user_message === "string" ? node.data.user_message : ""}
                    size={"small"}
                    fullWidth
                    placeholder={"Type your first message to start the AI conversation"}
                    multiline
                    minRows={3}
                    maxRows={9}
                    onClick={() => setExpandUserInputMessage(true)}
                    helperText={"This message will initiate the AI response and set the conversation context"}
                />
            </Box>

            <Box sx={{px: 1}}>
                <Stack direction={"row"} gap={2} alignItems={"center"} justifyContent={"space-between"}>
                    <Typography fontWeight={600} fontSize={13}>Temperature</Typography>

                    <Typography fontSize={12}>{node?.data?.temp?.toFixed(2) || 0}</Typography>
                </Stack>
                <Slider
                    sx={{flex: 1}} size={"small"}
                    value={node?.data?.temp || 0}
                    min={0} max={1} step={0.05}
                    onChange={e => onChangeData("temp", e.target.value)}
                    onBlur={() => onUpdate(node)}
                />
            </Box>

            <Box sx={{px: 1}}>
                <Stack direction={"row"} gap={2} alignItems={"center"} justifyContent={"space-between"}>
                    <Typography fontWeight={600} fontSize={13}>MAX Output Tokens</Typography>

                    <Typography fontSize={12}>{node?.data?.max_output_tokens || 0}</Typography>
                </Stack>
                <Slider
                    sx={{flex: 1}} size={"small"}
                    value={node?.data?.max_output_tokens || 0}
                    min={0} max={1024} step={1}
                    onChange={e => onChangeData("max_output_tokens", e.target.value)}
                    onBlur={() => onUpdate(node)}
                />
            </Box>
            <TextField
                type={"number"}
                value={node?.data?.conversation_buffer_window}
                size={"small"}
                label={"Conversation buffer window"}
                placeholder={"Buffer window"}
                // helperText={"Ask for confirmation"}
                onChange={(e) => onChangeData('conversation_buffer_window', e.target.value)}
                onBlur={() => onUpdate(node)}
            />
            <UserInputSettings node={node} onUpdate={onUpdate} onChangeData={onChangeData}/>

            <WatchdogSelection
                node={node} setNode={setNode}
                onUpdate={onUpdate} onChangeData={onChangeData}
                project={project} nodes={nodes}
            />

            <CustomParamData
                node={node} setNode={setNode} project={project}
                nodes={nodes}
                onChangeData={onChangeData}
                onUpdate={onUpdate}
            />
        </>
    )
}

const CustomParamData = ({node, setNode, project, onChangeData, onUpdate}) => {

    return (
        <>
            <Stack direction={"row"} gap={2} justifyContent={"space-between"} alignItems={"center"}>
                <Typography fontWeight={700} fontSize={13}>Custom Params</Typography>

                <Tooltip title={"Add Custom Param"}>
                    <IconButton
                        variant={"outlined"} color={"secondary"} size={"small"}
                        onClick={() => {
                            setNode(prev => {
                                const state = {...prev};
                                if (!state?.data?.custom_params) {
                                    state.data.custom_params = {}
                                }
                                const id = uuidv4();
                                state.data.custom_params[id] = "";
                                return state
                            })
                        }}
                    >
                        <IconPlus size={18}/>
                    </IconButton>
                </Tooltip>
            </Stack>
            {
                node?.data?.custom_params &&
                Object.keys(node?.data?.custom_params)?.sort()
                    ?.map((custom_param_key, index) => {
                        return (
                            <Stack
                                direction={"row"}
                                gap={1}
                                key={custom_param_key}
                            >
                                <TextField
                                    size={"small"}
                                    min={0}
                                    defaultValue={custom_param_key}
                                    InputLabelProps={{shrink: true}}
                                    sx={{flex: 1}}
                                    onBlur={(e) => {
                                        setNode(prev => {
                                            const state = {...prev};
                                            if (!state?.data?.custom_params) {
                                                state.data.custom_params = {}
                                            }
                                            const value = state.data.custom_params[custom_param_key] || "";
                                            delete state.data.custom_params[custom_param_key];
                                            state.data.custom_params[e.target.value] = value;
                                            onUpdate(state)
                                            return state
                                        })
                                    }}
                                />

                                <TextField
                                    size={"small"}
                                    min={0}
                                    value={node.data.custom_params[custom_param_key] || ""}
                                    InputLabelProps={{shrink: true}}
                                    sx={{flex: 1}}
                                    onChange={(e) => {
                                        setNode(prev => {
                                            const state = {...prev};
                                            if (!state?.data?.custom_params) {
                                                state.data.custom_params = {}
                                            }
                                            state.data.custom_params[custom_param_key] = e.target.value;
                                            return state
                                        })
                                    }}
                                    onBlur={() => onUpdate(node)}
                                />
                                <IconButton
                                    onClick={() => {
                                        setNode(prev => {
                                            const state = {...prev};
                                            delete state.data.custom_params[custom_param_key];
                                            return state
                                        })
                                    }}
                                >
                                    <IconX size={15}/>
                                </IconButton>
                            </Stack>
                        )
                    })
            }

        </>
    )
}
const SystemPromptVariables = ({node, setNode, project, onChangeData, onUpdate}) => {

    return (
        <Stack direction={"column"} gap={1}>
            <Stack direction={"row"} gap={2} justifyContent={"space-between"} alignItems={"center"}>
                <Typography fontWeight={700} fontSize={13}>System Prompt Variables</Typography>

                <Tooltip title={"Add Custom Param"}>
                    <IconButton
                        variant={"outlined"} color={"secondary"} size={"small"}
                        onClick={() => {
                            setNode(prev => {
                                const state = {...prev};
                                if (!state?.data?.system_prompt_variables) {
                                    state.data.system_prompt_variables = []
                                }
                                state.data.system_prompt_variables.push("");
                                return state
                            })
                        }}
                    >
                        <IconPlus size={18}/>
                    </IconButton>
                </Tooltip>
            </Stack>
            {(!node?.data?.system_prompt_variables || node?.data?.system_prompt_variables?.length === 0) &&
                <Typography>Please add variables</Typography>}
            {
                node?.data?.system_prompt_variables
                    ?.map((variable, index) => {
                        return (
                            <Stack
                                direction={"row"}
                                gap={1}
                                key={variable}
                            >
                                <TextField
                                    size={"small"}
                                    min={0}
                                    defaultValue={variable}
                                    InputLabelProps={{shrink: true}}
                                    placeholder={"Enter variable name"}
                                    sx={{flex: 1}}
                                    onBlur={(e) => {
                                        setNode(prev => {
                                            const state = {...prev};
                                            if (!state?.data?.system_prompt_variables) {
                                                state.data.system_prompt_variables = []
                                            }
                                            state.data.system_prompt_variables[index] = e.target.value || "";
                                            onUpdate(state)
                                            return state
                                        })
                                    }}
                                />

                                <IconButton
                                    onClick={() => {
                                        setNode(prev => {
                                            const state = {...prev};
                                            state.data.system_prompt_variables = state?.data?.system_prompt_variables?.filter((i, ind) => ind !== index);
                                            return state
                                        })
                                    }}
                                >
                                    <IconX size={15}/>
                                </IconButton>
                            </Stack>
                        )
                    })
            }

        </Stack>
    )
}